import React, { useState, useCallback } from 'react';
// Import icons from the UI package now
import { Button, Card, CardContent, CardDescription, CardHeader, Input, Loader2 } from "@pagespeedify/ui";

interface AddMemberFormProps {
  onSubmit: (email: string) => void;
  isAdding: boolean;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 100;

export const AddMemberForm: React.FC<AddMemberFormProps> = ({ onSubmit, isAdding }) => {
  const [memberEmail, setMemberEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = useCallback((email: string) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      return 'Email cannot be empty.';
    }
    if (trimmedEmail.length > MAX_EMAIL_LENGTH) {
      return `Email cannot exceed ${MAX_EMAIL_LENGTH} characters.`;
    }
    if (!emailRegex.test(trimmedEmail)) {
      return 'Please enter a valid email address.';
    }
    return null;
  }, []);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = event.target.value;
    setMemberEmail(newEmail);
    setEmailError(validateEmail(newEmail));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const error = validateEmail(memberEmail);
    setEmailError(error);

    if (!error) {
      onSubmit(memberEmail.trim());
      setMemberEmail('');
      setEmailError(null);
    }
  };

  const isSubmitDisabled = isAdding || !!emailError || !memberEmail.trim();

  return (
    <Card>
      {/* Reduced header padding */}
      <CardHeader className="p-4 pb-2">
        <CardDescription>Enter the email address of the member you want to add.</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        {/* Reduced content padding */}
        <CardContent className="p-4 pt-0">
          {/* Adjusted layout for input width */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-2 space-y-2 sm:space-y-0">
            {/* Container for input and error message */}
            <div className="flex-grow sm:flex-grow-0 sm:w-64 md:w-80"> {/* Constrain width on larger screens */}
              <Input
                type="email"
                id="memberEmail"
                value={memberEmail}
                onChange={handleEmailChange}
                placeholder="Enter member's email"
                required
                disabled={isAdding}
                className="w-full" // Input takes full width of its container
                aria-label="New Member Email"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "email-error" : undefined}
                maxLength={MAX_EMAIL_LENGTH}
              />
              {/* Display validation error inline */}
              {emailError && (
                <p id="email-error" className="pt-1 text-sm text-red-600">
                  {emailError}
                </p>
              )}
            </div>
            {/* Button takes remaining space on small screens, auto on larger */}
            <Button type="submit" disabled={isSubmitDisabled} className="w-full sm:w-auto">
              {isAdding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Member'
              )}
            </Button>
          </div>
        </CardContent>
      </form>
    </Card>
  );
};